import React, { memo } from 'react';
import { Info } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Combobox } from './ui/combobox';
import { DatePicker } from './ui/date-picker';
import { Label } from './ui/label';
import { cn } from '../lib/utils';

// Polyfill: timezone-local ISO string (preserves local date, ignores time)
if (!Date.prototype.toLocalISOString) {
  (function () {
    function pad(n) { return n < 10 ? '0' + n : n; }
    Date.prototype.toLocalISOString = function () {
      return this.getFullYear() + '-' + pad(this.getMonth() + 1) + '-' + pad(this.getDate()) + 'T00:00:00.000Z';
    };
  }());
}

function splitStringsInArray(inputArray, separator) {
  const newValues = [];
  const uniqueValues = new Set();
  inputArray.forEach(function (v) {
    if (v !== undefined && v !== null) {
      if (typeof v === 'string') {
        v.split(separator).forEach(function (t) {
          t = t.trim();
          if (!uniqueValues.has(t)) { newValues.push(t); uniqueValues.add(t); }
        });
      } else {
        newValues.push(v);
      }
    }
  });
  return newValues;
}

function Explanation({ explanation }) {
  if (!explanation) return null;
  return (
    <p className="flex items-start gap-1.5 text-sm text-gray-500 mt-1 mb-3">
      <Info className="w-4 h-4 shrink-0 mt-0.5 text-primary-900/60" />
      <span>
        <ReactMarkdown allowedElements={['strong', 'em', 'a']} unwrapDisallowed={true}>
          {explanation}
        </ReactMarkdown>
      </span>
    </p>
  );
}

function PlaceholderLink({ allowPreliminary, preliminary, onClick }) {
  if (!allowPreliminary) return null;
  const text = preliminary ? 'unmark as preliminary' : 'mark as preliminary';
  return (
    <a
      onClick={onClick}
      className="block text-right text-xs text-primary-900/60 hover:text-primary-900 cursor-pointer mt-1"
    >
      {text}
    </a>
  );
}

// Convert ISO date string to DD/MM/YYYY for the date picker display
function isoToDisplay(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

// Convert DD/MM/YYYY from date picker back to ISO
function displayToIso(displayStr) {
  if (!displayStr) return undefined;
  const trimmed = displayStr.trim();
  // Accept ISO input (yyyy-mm-dd) directly, e.g. when the value is typed rather than picked.
  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return isNaN(d.getTime()) ? undefined : d.toLocalISOString();
  }
  const parts = trimmed.split('/');
  if (parts.length !== 3) return undefined;
  const [day, month, year] = parts;
  const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  if (isNaN(d.getTime())) return undefined;
  return d.toLocalISOString();
}

// Transform options from {text, value, key} to react-select {label, value}
function toSelectOptions(options) {
  if (!options) return [];
  return options.map((o) => ({ label: o.text, value: o.value }));
}

// Transform react-select value back to plain value(s)
function fromSelectValue(selected, multiple) {
  if (selected === null || selected === undefined) return multiple ? [] : undefined;
  if (multiple) return Array.isArray(selected) ? selected.map((o) => o.value) : [];
  return selected.value;
}

// Build react-select value from current app value + options
function toSelectValue(value, options, multiple) {
  const opts = toSelectOptions(options);
  if (multiple) {
    if (!value || !Array.isArray(value)) return [];
    return value.map((v) => opts.find((o) => o.value === v) || { label: String(v), value: v });
  }
  if (value === undefined || value === null) return null;
  return opts.find((o) => o.value === value) || { label: String(value), value };
}

function FieldWrapper({ className, preliminary, preliminarySection, children }) {
  return (
    <div
      className={cn(
        'field py-2',
        // Preliminary answers are provisional, not wrong — marked with the info
        // (LCSB Sky) rail, matching PreliminarySection. Danger stays for errors.
        (preliminary || preliminarySection) && 'border-l-2 border-info-900 pl-3 opacity-75',
        className
      )}
      data-testid="field-group"
    >
      {children}
    </div>
  );
}

function ItemField({
  error: errorProp,
  preliminarySection,
  type: typeProp,
  options: optionsProp,
  label,
  defaultValue,
  value: valueProp,
  multiple,
  allowAdditions,
  allowPreliminary,
  preliminary,
  min,
  itemKey,
  explanation,
  optional,
  focused,
  readOnly,
  isObserver,
  handleChange,
  handleAddition,
  togglePreliminary,
  onEnter,
  loopName: _loopName,
  ...rest
}) {
  // Resolve default value
  let value = valueProp;
  if ((value === undefined || value === null) && defaultValue) {
    value = defaultValue;
    Promise.resolve().then(() => {
      if (!readOnly && !isObserver) handleChange(value);
    });
  }

  const error = typeof errorProp === 'object' && errorProp !== null ? errorProp.text : errorProp;

  let type = typeProp;
  let options = optionsProp;
  if (type === 'yesno') {
    type = 'radio';
    options = [
      { key: itemKey + 'y', text: 'yes', value: 1 },
      { key: itemKey + 'n', text: 'no', value: 0 },
    ];
  }

  const onChange = (newValue) => {
    if (readOnly || isObserver) return;
    handleChange(newValue);
  };

  const onTogglePreliminary = () => {
    if (readOnly || isObserver) return;
    togglePreliminary();
  };

  // Enter advances to the next item. Skipped for textarea (needs newlines) and
  // for the dropdown/date widgets, which use Enter for their own interactions.
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && onEnter) {
      e.preventDefault();
      onEnter();
    }
  };

  const fieldLabel = label && (
    <Label className={cn('mb-2', optional ? 'optional-field' : 'mandatory-field')}>
      <ReactMarkdown allowedElements={['strong', 'em', 'a']} unwrapDisallowed={true}>
        {label}
      </ReactMarkdown>
    </Label>
  );

  switch (type) {
    case 'noInput': {
      const content = label || '' + (error || '');
      return (
        <FieldWrapper preliminary={preliminary} preliminarySection={preliminarySection}>
          <p>
            <ReactMarkdown allowedElements={['strong', 'em', 'a']} unwrapDisallowed={true}>
              {content}
            </ReactMarkdown>
          </p>
        </FieldWrapper>
      );
    }

    case 'radio':
      return (
        <FieldWrapper preliminary={preliminary} preliminarySection={preliminarySection}>
          {fieldLabel}
          <Explanation explanation={explanation} />
          <RadioGroup
            value={String(value ?? '')}
            onKeyDown={onKeyDown}
            onValueChange={(v) => {
              // Convert back to number if original options use numeric values
              const opt = options.find((o) => String(o.value) === v);
              onChange(opt ? opt.value : v);
            }}
          >
            {options.map((option) => (
              <div key={itemKey + option.key} className="flex items-center gap-2">
                <RadioGroupItem
                  id={`${itemKey}-${option.key}`}
                  value={String(option.value)}
                  autoFocus={focused && options.indexOf(option) === 0}
                />
                <label htmlFor={`${itemKey}-${option.key}`} className="text-sm cursor-pointer">
                  {option.text}
                </label>
              </div>
            ))}
          </RadioGroup>
          {error && <p className="text-xs text-danger-900 mt-1">{error}</p>}
          {!preliminarySection && (
            <PlaceholderLink allowPreliminary={allowPreliminary} preliminary={preliminary} onClick={onTogglePreliminary} />
          )}
        </FieldWrapper>
      );

    case 'checkbox':
      return (
        <FieldWrapper preliminary={preliminary} preliminarySection={preliminarySection}>
          <div className="flex items-start gap-2">
            <Checkbox
              id={`checkbox-${itemKey}`}
              checked={!!value}
              onCheckedChange={() => {
                if (readOnly || isObserver) return;
                handleChange(!value);
              }}
              autoFocus={focused}
            />
            <label htmlFor={`checkbox-${itemKey}`} className="text-sm cursor-pointer leading-snug">
              {label}
            </label>
          </div>
          {error && <p className="text-xs text-danger-900 mt-1">{error}</p>}
          <Explanation explanation={explanation} />
          {!preliminarySection && (
            <PlaceholderLink allowPreliminary={allowPreliminary} preliminary={preliminary} onClick={onTogglePreliminary} />
          )}
        </FieldWrapper>
      );

    case 'text':
      return (
        <FieldWrapper preliminary={preliminary} preliminarySection={preliminarySection}>
          {fieldLabel}
          <Explanation explanation={explanation} />
          <Input
            autoFocus={focused}
            error={!!error}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
          />
          {error && <p className="text-xs text-danger-900 mt-1">{error}</p>}
          {!preliminarySection && (
            <PlaceholderLink allowPreliminary={allowPreliminary} preliminary={preliminary} onClick={onTogglePreliminary} />
          )}
        </FieldWrapper>
      );

    case 'number':
      return (
        <FieldWrapper preliminary={preliminary} preliminarySection={preliminarySection}>
          {fieldLabel}
          <Explanation explanation={explanation} />
          <Input
            autoFocus={focused}
            error={!!error}
            type="number"
            value={value ?? ''}
            min={min || ''}
            onChange={(e) => onChange(parseInt(e.target.value))}
            onKeyDown={onKeyDown}
          />
          {error && <p className="text-xs text-danger-900 mt-1">{error}</p>}
          {!preliminarySection && (
            <PlaceholderLink allowPreliminary={allowPreliminary} preliminary={preliminary} onClick={onTogglePreliminary} />
          )}
        </FieldWrapper>
      );

    case 'textarea':
      return (
        <FieldWrapper preliminary={preliminary} preliminarySection={preliminarySection}>
          {fieldLabel}
          <Explanation explanation={explanation} />
          <Textarea
            autoFocus={focused}
            error={!!error}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
          {error && <p className="text-xs text-danger-900 mt-1">{error}</p>}
          {!preliminarySection && (
            <PlaceholderLink allowPreliminary={allowPreliminary} preliminary={preliminary} onClick={onTogglePreliminary} />
          )}
        </FieldWrapper>
      );

    case 'date':
      return (
        <FieldWrapper preliminary={preliminary} preliminarySection={preliminarySection}>
          {fieldLabel}
          <Explanation explanation={explanation} />
          <DatePicker
            autoFocus={focused}
            error={!!error}
            value={isoToDisplay(value)}
            onChange={(displayStr) => {
              const iso = displayToIso(displayStr);
              if (iso !== value) onChange(iso);
            }}
          />
          {error && <p className="text-xs text-danger-900 mt-1">{error}</p>}
        </FieldWrapper>
      );

    case 'dropdown': {
      const uniqueOptionsValues = new Set(options ? options.map((o) => o.value) : []);
      let effectiveOptions = options ? [...options] : [];

      if (allowAdditions && value) {
        if (multiple) {
          (Array.isArray(value) ? value : [value]).forEach((v) => {
            if (!uniqueOptionsValues.has(v)) {
              effectiveOptions.push({ text: v, value: v });
              uniqueOptionsValues.add(v);
            }
          });
        } else if (!uniqueOptionsValues.has(value)) {
          effectiveOptions.push({ text: value, value });
        }
      }

      const placeholder = allowAdditions ? 'Choose an option or type a new value' : 'Choose an option';
      const selectOptions = toSelectOptions(effectiveOptions);
      const selectValue = toSelectValue(value, effectiveOptions, multiple);

      return (
        <FieldWrapper preliminary={preliminary} preliminarySection={preliminarySection}>
          {fieldLabel}
          <Explanation explanation={explanation} />
          <Combobox
            autoFocus={focused}
            error={!!error}
            options={selectOptions}
            value={selectValue}
            isMulti={multiple}
            allowAdditions={allowAdditions}
            placeholder={placeholder}
            onChange={(selected) => {
              const newVal = fromSelectValue(selected, multiple);
              if (multiple && Array.isArray(newVal)) {
                let processed = newVal;
                const separators = [',', ';'];
                for (const sep of separators) {
                  processed = splitStringsInArray(processed, sep);
                }
                onChange(processed);
              } else {
                onChange(newVal);
              }
            }}
            onCreateOption={allowAdditions ? (inputValue) => {
              if (handleAddition) handleAddition(inputValue);
            } : undefined}
          />
          {error && <p className="text-xs text-danger-900 mt-1">{error}</p>}
          {!preliminarySection && (
            <PlaceholderLink allowPreliminary={allowPreliminary} preliminary={preliminary} onClick={onTogglePreliminary} />
          )}
        </FieldWrapper>
      );
    }

    default:
      return null;
  }
}

export { ItemField };
